import axios from 'axios';
import type { AbpErrorResponse, AbpRemoteServiceError } from '@/api/types/abp';

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string | null;
  readonly details?: string | null;
  readonly validationErrors?: AbpRemoteServiceError['validationErrors'];

  constructor(status: number, remote: AbpRemoteServiceError) {
    super(remote.message || 'An unexpected error occurred.');
    this.name = 'ApiError';
    this.status = status;
    this.code = remote.code;
    this.details = remote.details;
    this.validationErrors = remote.validationErrors;
  }
}

// Normalize any thrown value into an ApiError carrying the ABP error envelope
// when present. Never surfaces raw server internals to the UI beyond message.
export function toApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;

  if (axios.isAxiosError(err)) {
    const status = err.response?.status ?? 0;
    const body = err.response?.data as AbpErrorResponse | undefined;
    if (body?.error) return new ApiError(status, body.error);
    if (status === 0) {
      return new ApiError(0, {
        message: 'Could not reach the server. Check your connection and try again.',
      });
    }
    return new ApiError(status, { message: err.message });
  }

  return new ApiError(0, { message: err instanceof Error ? err.message : 'Unknown error' });
}
